/**
 * ActivityTooltip
 *
 * 주간 활동 원 호버 시 표시되는 툴팁
 * - 주 번호
 * - 총 조회수
 * - 가중 참여율 (댓글×2 + 리포스트×3 + 좋아요)
 * - 스레드 수
 */

import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

/**
 * 숫자 포맷팅
 */
const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

const ActivityTooltip = ({
  week,
  position,
  containerRef,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!week || !position) return null;

  // 툴팁 위치 계산 (커서 우측 상단에 배치하여 시야 방해 방지)
  const tooltipStyle = {
    position: 'absolute',
    left: position.x + 16,
    top: position.y - 60,
    transform: 'translateY(0)',
    pointerEvents: 'none',
    zIndex: 100,
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 5 }}
        transition={{ duration: 0.15 }}
        style={tooltipStyle}
      >
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            minWidth: 140,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* 주 번호 */}
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"source-han-serif-korean", "Noto Serif KR", serif',
              fontWeight: 600,
              color: 'text.primary',
              display: 'block',
              mb: 1,
            }}
          >
            {t('yearEnd.activity.weekLabel', {
              week: week.weekNumber,
              defaultValue: `${week.weekNumber}주차`,
            })}
          </Typography>

          {/* 통계 */}
          <Stack spacing={0.5}>
            {/* 총 조회수 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('yearEnd.activity.tooltip.views', { defaultValue: '조회수' })}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {formatNumber(week.totalViews)}
              </Typography>
            </Stack>

            {/* 가중 참여율 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('yearEnd.activity.tooltip.engagement', { defaultValue: '참여' })}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {formatNumber(week.weightedEngagement)}
              </Typography>
            </Stack>

            {/* 스레드 수 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('yearEnd.activity.tooltip.threads', { defaultValue: '스레드' })}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {week.threadCount}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};

export default ActivityTooltip;
